import {
	ArrayInput,
	BooleanInput,
	Confirm,
	Create,
	ReferenceInput,
	SelectInput,
	SimpleForm,
	SimpleFormIterator,
	TextInput,
	maxLength,
	required,
	useGetList,
	usePermissions,
	useSimpleFormIteratorItem,
} from 'react-admin';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { parseCurrency } from '../../utils';

const PlaceSelectInput = ({ places, arraySource }) => {
	const { index } = useSimpleFormIteratorItem();
	const allCodes = useWatch({ name: arraySource }) || [];
	const usedPlaceIds = allCodes
		.filter((c, i) => i !== index && c?.placeId)
		.map(c => c.placeId);
	const availablePlaces = places.filter(p => !usedPlaceIds.includes(p.id));
	return (
		<SelectInput
			source="placeId"
			label="Posto"
			choices={availablePlaces}
			optionText="name"
			optionValue="id"
			helperText={false}
		/>
	);
};

const BulkApplyCode = ({ arraySource }) => {
	const { setValue, getValues } = useFormContext();
	const rows = useWatch({ name: arraySource }) || [];
	const [bulkCode, setBulkCode] = useState('');
	const [confirmOpen, setConfirmOpen] = useState(false);

	const hasRows = rows.some(c => c?.placeId);

	const apply = () => {
		const code = bulkCode.trim();
		const current = getValues(arraySource) || [];
		setValue(
			arraySource,
			current.map(c => ({ ...c, code })),
			{ shouldDirty: true, shouldTouch: true }
		);
		setBulkCode('');
	};

	const handleClick = () => {
		if (!bulkCode.trim() || !hasRows) return;
		const alreadyHasCode = rows.some(c => c?.code);
		if (alreadyHasCode) {
			setConfirmOpen(true);
		} else {
			apply();
		}
	};

	const handleConfirm = () => {
		apply();
		setConfirmOpen(false);
	};

	return (
		<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={2}>
			<TextField
				size="small"
				label="Código para os postos abaixo"
				value={bulkCode}
				onChange={e => setBulkCode(e.target.value)}
			/>
			<Button variant="outlined" onClick={handleClick} disabled={!bulkCode.trim() || !hasRows}>
				Aplicar a todos
			</Button>
			<Confirm
				isOpen={confirmOpen}
				title="Aplicar código a todos os postos"
				content="Isso vai substituir o código dos postos já adicionados. Deseja continuar?"
				onConfirm={handleConfirm}
				onClose={() => setConfirmOpen(false)}
			/>
		</Stack>
	);
};

export default props => {
	const { permissions } = usePermissions();
	const { data: places = [] } = useGetList(`chains/${getChainId()}/places`, {
		sort: { field: 'name', order: 'ASC' },
	});

	const transform = (data) => ({
		...data,
		codes: (data.codes || []).filter(c => c?.placeId && c?.code),
	});

	return (
		<Permission permission={PERMISSIONS.CREATE_PRODUCT}>
			<Create {...props} title="Criar Produto" transform={transform}>
				<SimpleForm>
					<Grid container spacing={2} sx={{ maxWidth: 800 }}>
						<Grid size={12}>
							<TextInput
								source='code'
								label="Código do Produto (padrão da rede)"
								min={1}
								validate={[required(), maxLength(200)]}
								fullWidth
							/>
						</Grid>
						<Grid size={12}>
							<TextInput
								source="name"
								label="Nome"
								slotProps={{ input: { autoComplete: 'off' } }}
								validate={[required(), maxLength(255)]}
								fullWidth
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<ReferenceInput
								source="groupId"
								reference='chains/chainId/product-categories'
								sort={{ field: 'name', order: 'ASC' }} >
								<SelectInput optionText="name" label="Categoria do Produto" fullWidth />
							</ReferenceInput>
						</Grid>
						{
							permissions && permissions.includes(PERMISSIONS.ADMIN) &&
							<Grid size={{ xs: 12, md: 6 }}>
								<ReferenceInput
									source="chainId"
									reference="chains"
									perPage={null}
									defaultValue={getChainId()}
									sort={{ field: 'name', order: 'ASC' }} >
									<SelectInput optionText="name" label="Rede" fullWidth />
								</ReferenceInput>
							</Grid>
						}
						<Grid size={{ xs: 12, md: 6 }}>
							<TextInput
								source="pointsPerMoney"
								label="Pontos por real"
								slotProps={{ input: { autoComplete: 'off' } }}
								format={(value) => {
									if (!value || value === 0) return "0,00";
									return `${Number(value).toFixed(2).replace('.', ',')}`;
								}}
								parse={(value) => {
									if (!value || value === '') return 0;
									return parseCurrency(value, 2);
								}}
								fullWidth
							/>
						</Grid>
						<Grid size={12}>
							<BooleanInput label="Aceita cashback" source="acceptsCashback" defaultValue={true} helperText="Desligado, o cashback do cliente não desconta este produto no caixa" />
						</Grid>
						<Grid size={12}>
							<Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
								<Typography variant="subtitle1" fontWeight={600} mb={0.5}>
									Códigos por Posto
								</Typography>
								<Typography variant="body2" color="text.secondary" mb={2}>
									Alguns postos podem usar um código diferente do padrão da rede cadastrado acima.
									Se nenhum posto for configurado aqui, todos os postos usarão o código padrão automaticamente.
								</Typography>
								<BulkApplyCode arraySource="codes" />
								<ArrayInput source="codes" label={false}>
									<SimpleFormIterator inline disableReordering>
										<PlaceSelectInput places={places} arraySource="codes" />
										<TextInput source="code" label="Código no Posto" helperText={false} />
									</SimpleFormIterator>
								</ArrayInput>
							</Box>
						</Grid>
					</Grid>
				</SimpleForm>
			</Create>
		</Permission>
	);
};
